import type { ComponentType } from "react";
import {
  AlertOctagon,
  Building2,
  FileText,
  ShieldCheck,
  Stethoscope,
  User,
} from "lucide-react";

import type { FieldRow } from "@/api/types";
import { CodePill, type CodeSystem } from "@/components/CodePill";

// Render code-list values as discrete pills instead of a comma-joined
// string. The backend signals these via the field key — kept in sync
// with services/fields.py:_CODE_LIST_KEYS plus the single-value denial
// codes whose description surfaces via the CodePill tooltip.
const CODE_SYSTEM_BY_FIELD: Record<string, CodeSystem> = {
  principal_diagnosis: "icd10",
  icd10_diagnoses: "icd10",
  icd10_pcs_procedures: "icd10",
  cpt_codes: "cpt",
  auth_cpt_codes: "cpt",
  carc_code: "carc",
  rarc_code: "rarc",
};

// Keys whose value is a newline-formatted multi-line string (addresses).
const MULTILINE_FIELDS = new Set([
  "patient_address",
  "provider_address",
  "payer_address",
]);

const GROUP_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  Patient: User,
  Claim: FileText,
  Provider: Stethoscope,
  Payer: Building2,
  Authorization: ShieldCheck,
  Denial: AlertOctagon,
};

type LineAdjustmentItem = {
  group_code?: string | null;
  carc_code?: string | null;
  amount?: string | null;
};

type ServiceLineItem = {
  revenue_code?: string | null;
  cpt_hcpcs?: string | null;
  modifiers?: string[] | null;
  description?: string | null;
  units?: number | null;
  charge?: string | null;
  dos?: string | null;
  adjustments?: LineAdjustmentItem[] | null;
  remarks?: string[] | null;
};

export function FieldsPanel({
  fields,
  maxHeight,
}: {
  fields: FieldRow[];
  maxHeight?: string;
}) {
  // Group rows by ``group`` while preserving the backend-supplied
  // order (the registry is already ordered semantically).
  const grouped: Array<{ name: string; rows: FieldRow[] }> = [];
  for (const row of fields) {
    const last = grouped[grouped.length - 1];
    if (last && last.name === row.group) last.rows.push(row);
    else grouped.push({ name: row.group, rows: [row] });
  }

  return (
    <div
      className="space-y-3 overflow-y-auto pr-1"
      style={maxHeight ? { maxHeight } : undefined}
    >
      {grouped.map((g) => {
        const Icon = GROUP_ICONS[g.name] ?? FileText;
        return (
          <div
            key={g.name}
            className="bg-white border rounded-lg shadow-sm overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-slate-50/60">
              <Icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {g.name}
              </span>
              <span className="ml-auto text-[11px] font-mono text-muted-foreground/70">
                {g.rows.length}
              </span>
            </div>
            <dl className="divide-y">
              {g.rows.map((row) => {
                const isServiceLines =
                  (row.key === "billed_lines" || row.key === "remit_lines") &&
                  Array.isArray(row.items);
                const multiline = MULTILINE_FIELDS.has(row.key);
                const stack = isServiceLines || multiline;
                return (
                  <div
                    key={row.key}
                    className={`grid grid-cols-[140px_1fr] gap-3 px-4 py-2 ${stack ? "items-start" : "items-baseline"}`}
                  >
                    <dt className="text-[12px] text-muted-foreground">
                      {row.label}
                    </dt>
                    <dd
                      className={`text-[13px] font-mono ${multiline ? "whitespace-pre-line break-words" : isServiceLines ? "" : "break-all"}`}
                    >
                      {isServiceLines ? (
                        <ServiceLinesTable
                          items={row.items as unknown as ServiceLineItem[]}
                        />
                      ) : (
                        <FieldValue fieldKey={row.key} value={row.value} />
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

function FieldValue({ fieldKey, value }: { fieldKey: string; value: string }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const system = CODE_SYSTEM_BY_FIELD[fieldKey];
  if (system) {
    const codes = value
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    return (
      <div className="flex flex-wrap gap-1.5">
        {codes.map((code) => (
          <CodePill key={code} system={system} code={code} />
        ))}
      </div>
    );
  }
  return <span>{value}</span>;
}

function ServiceLinesTable({ items }: { items: ServiceLineItem[] }) {
  if (!items.length) {
    return <span className="text-muted-foreground">—</span>;
  }
  // Decide which optional columns to show based on whether any row uses them.
  const showDos = items.some((it) => it.dos);
  const showUnits = items.some((it) => it.units != null);
  const showAdjustments = items.some(
    (it) => (it.adjustments && it.adjustments.length) || (it.remarks && it.remarks.length),
  );
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full text-[12px] font-mono">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
            <th className="text-left font-medium px-1.5 py-1">Rev</th>
            <th className="text-left font-medium px-1.5 py-1">CPT / HCPCS</th>
            <th className="text-left font-medium px-1.5 py-1">Description</th>
            {showDos && (
              <th className="text-left font-medium px-1.5 py-1">DOS</th>
            )}
            {showUnits && (
              <th className="text-right font-medium px-1.5 py-1">Units</th>
            )}
            <th className="text-right font-medium px-1.5 py-1">Charge</th>
            {showAdjustments && (
              <th className="text-left font-medium px-1.5 py-1">Adjustments</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it, i) => (
            <tr key={i} className="align-top">
              <td className="px-1.5 py-1.5 text-slate-600">
                {it.revenue_code || "—"}
              </td>
              <td className="px-1.5 py-1.5">
                <CptCell code={it.cpt_hcpcs} modifiers={it.modifiers} />
              </td>
              <td className="px-1.5 py-1.5 text-slate-700 break-words">
                {it.description || "—"}
              </td>
              {showDos && (
                <td className="px-1.5 py-1.5 text-slate-600 whitespace-nowrap">
                  {it.dos || "—"}
                </td>
              )}
              {showUnits && (
                <td className="px-1.5 py-1.5 text-right tabular-nums">
                  {it.units ?? "—"}
                </td>
              )}
              <td className="px-1.5 py-1.5 text-right tabular-nums">
                {it.charge ? `$${it.charge}` : "—"}
              </td>
              {showAdjustments && (
                <td className="px-1.5 py-1.5">
                  <AdjustmentsCell
                    adjustments={it.adjustments}
                    remarks={it.remarks}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdjustmentsCell({
  adjustments,
  remarks,
}: {
  adjustments?: LineAdjustmentItem[] | null;
  remarks?: string[] | null;
}) {
  const hasAdj = adjustments && adjustments.length > 0;
  const hasRarc = remarks && remarks.length > 0;
  if (!hasAdj && !hasRarc) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {hasAdj &&
        adjustments!.map((adj, i) => (
          <div key={`adj-${i}`} className="flex flex-wrap items-center gap-1">
            {adj.group_code && <CodePill system="group" code={adj.group_code} />}
            {adj.carc_code && <CodePill system="carc" code={adj.carc_code} />}
            {adj.amount && (
              <span className="text-[11px] text-slate-500 tabular-nums">
                ${adj.amount}
              </span>
            )}
          </div>
        ))}
      {hasRarc && (
        <div className="flex flex-wrap items-center gap-1">
          {remarks!.map((r) => (
            <CodePill key={r} system="rarc" code={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function CptCell({
  code,
  modifiers,
}: {
  code?: string | null;
  modifiers?: string[] | null;
}) {
  if (!code && (!modifiers || modifiers.length === 0)) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {code && <CodePill system="cpt" code={code} />}
      {modifiers?.filter(Boolean).map((m) => (
        <span
          key={m}
          className="inline-flex items-center rounded border px-1 py-0 text-[10px] font-medium text-slate-600 bg-slate-50"
        >
          -{m}
        </span>
      ))}
    </div>
  );
}
